import React, { useState } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Button,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Copy as ContentCopyIcon,
  Check as CheckIcon,
  ChevronDown,
  EyeOff,
} from 'lucide-mui';
import DownloadFile from '../../../selectedVideo/tabs/Download';

interface AIResultAccordionProps {
  onCopyContent: (text: string) => void;
  copySuccess: boolean;
  downloadText: string;
  fileName: string;
  hideReferences: boolean;
  setHideReferences: (hide: boolean) => void;
}

// Helper function to clean citation references
const cleanText = (text: string): string => {
  return text.replace(/\s*\[→\]\([^)]+\)\s*/g, '');
};

const AIResultAccordion: React.FC<AIResultAccordionProps> = ({
  onCopyContent,
  copySuccess,
  downloadText,
  fileName,
  hideReferences,
  setHideReferences,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const cleanedText = cleanText(downloadText);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyClick = () => {
    onCopyContent(cleanedText);
    handleClose();
  };

  const handleHideReferencesClick = () => {
    setHideReferences(!hideReferences);
    handleClose();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        variant="text"
        onClick={handleClick}
        endIcon={<ChevronDown />}
        sx={{
          height: '40px',
          textTransform: 'none',
          color: 'text.primary',
          borderRadius: '20px',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        Options
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleCopyClick}>
          <ListItemIcon>
            {copySuccess ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {copySuccess ? 'Copied!' : 'Copy Content'}
          </ListItemText>
        </MenuItem>
        <MenuItem>
          <DownloadFile text={cleanedText} fileName={fileName} variant="icon" />
        </MenuItem>
        <MenuItem onClick={handleHideReferencesClick}>
          <ListItemIcon>
            <EyeOff
              fontSize="small"
              color={hideReferences ? 'primary' : undefined}
            />
          </ListItemIcon>
          <ListItemText>
            {hideReferences ? 'Show References' : 'Hide References'}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AIResultAccordion;
